import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { a2zVendor, rentalCategories } from '../data/a2zRentalCatalog';
import './EquipmentRentalsPage.css';

const STORAGE_KEY = 'mepro_a2z_equipment_subscription';

function generatePartnerCode() {
  const a = crypto.getRandomValues(new Uint8Array(4));
  const b = crypto.getRandomValues(new Uint8Array(4));
  const toHex = (arr) =>
    Array.from(arr, (x) => x.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
  return `A2Z-${toHex(a).slice(0, 4)}-${toHex(b).slice(0, 4)}`;
}

export default function EquipmentRentalsPage() {
  const navigate = useNavigate();
  const telHref = `tel:+91${a2zVendor.phone.replace(/\D/g, '')}`;

  const [subscribed, setSubscribed] = useState(false);
  const [partnerCode, setPartnerCode] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (typeof data.subscribed === 'boolean') {
          setSubscribed(data.subscribed);
        }
        if (data.partnerCode && typeof data.partnerCode === 'string') {
          setPartnerCode(data.partnerCode);
        } else if (data.subscribed) {
          setPartnerCode(generatePartnerCode());
        }
      }
    } catch (_) {
      /* ignore */
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ subscribed, partnerCode })
      );
    } catch (_) {
      /* ignore */
    }
  }, [hydrated, subscribed, partnerCode]);

  const setSubscribedOn = useCallback(() => {
    setPartnerCode((prev) => prev || generatePartnerCode());
    setSubscribed(true);
  }, []);

  const setSubscribedOff = useCallback(() => {
    setSubscribed(false);
  }, []);

  return (
    <div className="equipment-page">
      <div className="equipment-page-inner">
        <div className="equipment-back-row">
          <button type="button" className="equipment-back" onClick={() => navigate('/production-sourcing')}>
            ← All suppliers
          </button>
          <span className="equipment-back-sep" aria-hidden>
            ·
          </span>
          <button type="button" className="equipment-back" onClick={() => navigate('/')}>
            Home
          </button>
        </div>

        <header className="equipment-vendor-card">
          <div className="equipment-vendor-header-row">
            <div className="equipment-vendor-main">
              <h1>{a2zVendor.name}</h1>
              <p className="equipment-vendor-meta">{a2zVendor.address}</p>
              <p className="equipment-vendor-meta">
                Phone:{' '}
                <a href={telHref}>{a2zVendor.phone}</a>
              </p>
              <p className="equipment-disclaimer">
                Rates as on supplier sheet; confirm availability and terms directly with the vendor.
              </p>
            </div>

            <aside className="equipment-subscribe-panel" aria-label="Subscription">
              <p className="equipment-subscribe-title">Subscription</p>
              <div className="equipment-toggle" role="group" aria-label="Choose subscription status">
                <button
                  type="button"
                  className={`equipment-toggle-btn ${!subscribed ? 'equipment-toggle-btn--active' : ''}`}
                  onClick={setSubscribedOff}
                  aria-pressed={!subscribed}
                >
                  Unsubscribed
                </button>
                <button
                  type="button"
                  className={`equipment-toggle-btn ${subscribed ? 'equipment-toggle-btn--active' : ''}`}
                  onClick={setSubscribedOn}
                  aria-pressed={subscribed}
                >
                  Subscribed
                </button>
              </div>
              {subscribed && partnerCode && (
                <div className="equipment-partner-code">
                  <span className="equipment-partner-code-label">Your unique code</span>
                  <div className="equipment-partner-code-value" title="Save this code for bookings">
                    {partnerCode}
                  </div>
                  <p className="equipment-partner-code-hint">
                    Use this code when you contact A2Z for rentals (stored on this device).
                  </p>
                </div>
              )}
            </aside>
          </div>
        </header>

        <nav className="equipment-nav" aria-label="Jump to category">
          {rentalCategories.map((cat) => (
            <a key={cat.id} href={`#${cat.id}`}>
              {cat.title}
            </a>
          ))}
        </nav>

        {rentalCategories.map((cat) => (
          <section key={cat.id} id={cat.id} className="equipment-section">
            <h2 className="equipment-section-title">{cat.title}</h2>
            <div className="equipment-table-wrap">
              <table className="equipment-table">
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>Brand</th>
                    <th>{cat.itemColumn}</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {cat.rows.map((row, idx) => (
                    <tr key={`${cat.id}-${idx}`}>
                      <td>{row.sno}</td>
                      <td>{row.brand}</td>
                      <td>{row.item}</td>
                      <td>{row.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
